import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GenericField } from './generic-field.model';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

@Component({
  selector: 'app-form-generic',
  templateUrl: './form-generic.component.html',
  styleUrls: ['./form-generic.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    ReactiveFormsModule,
    FormsModule, 
    MatOptionModule,
    MatAutocompleteModule
  ]
})
export class FormGenericComponent implements OnInit, OnChanges {
  @Input() isEdit: boolean = false;
  @Input() icon: string = 'add_circle';
  @Input() title: string = 'Registrar';
  @Input() subtitle: string = 'Complete todos los campos antes de registrar.';
  @Input() actionTextCreate: string = 'Registrar';
  @Input() actionTextEdit: string = 'Editar';

  @Input() fields: GenericField[] = [];
  @Input() initialData: any = null;

  /** ⭐ AGREGAMOS COMPATIBILIDAD TOTAL CON 5 COLUMNAS */
  @Input() columns: 1 | 2 | 3 | 4 | 5 = 1;

  @Output() submitForm = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();

  form!: FormGroup;
  loading: boolean = false;
  imagePreviews: { [key: string]: string | null } = {};
  imagePickerMode: { [key: string]: 'file' | 'url' } = {};

  constructor(private readonly fb: FormBuilder) { }

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields'] && !changes['fields'].firstChange) {
      this.buildForm();
    }

    if (changes['initialData'] && this.form && this.initialData) {
      this.safePatch(this.initialData);
    }
  }

  private buildForm(): void {
    const group: any = {};
    this.imagePreviews = {};

    this.fields.forEach((field) => {
      const value = this.initialData
        ? this.initialData[field.name] ?? null
        : null;

      group[field.name] = [
        { value, disabled: !!field.disabled },
        field.validators || [],
      ];

      if (field.type === 'file' && typeof value === 'string' && value.trim() !== '') {
          this.imagePreviews[field.name] = value;
      }
      
      if (field.type === 'mac-image-picker') {
          this.imagePickerMode[field.name] = 'url';
          if (typeof value === 'string' && value.trim() !== '') {
              this.imagePreviews[field.name] = value;
              if (value.startsWith('data:image')) {
                  this.imagePickerMode[field.name] = 'file';
              }
          }
      }
    });

    this.form = this.fb.group(group);

    if (this.initialData) {
      this.safePatch(this.initialData);
    }
  }

  filterOptions(fieldName: string, value: any): any[] {
    const field = this.fields.find(f => f.name === fieldName);
    if (!field || !field.options) return [];
    if (typeof value !== 'string') return field.options;
    
    const filterValue = value.toLowerCase();
    return field.options.filter(option => option.label.toLowerCase().includes(filterValue));
  }

  displayFn(fieldName: string): (val: any) => string {
    return (val: any) => {
      const field = this.fields.find(f => f.name === fieldName);
      if (!field || !field.options) return val;
      const selected = field.options.find(opt => opt.value === val);
      return selected ? selected.label : val;
    };
  }

  /** Patch seguro */
  private safePatch(data: any): void {
    if (!data || !this.form) return;

    Object.keys(data).forEach((key) => {
      if (this.form.controls[key]) {
        this.form.controls[key].setValue(data[key]);

        // Sincronizar previsualizaciones si hay datos de imágenes
        const field = this.fields.find(f => f.name === key);
        if (field && (field.type === 'file' || field.type === 'mac-image-picker')) {
          const value = data[key];
          if (typeof value === 'string' && value.trim() !== '') {
            this.imagePreviews[key] = value;
            if (field.type === 'mac-image-picker') {
                this.imagePickerMode[key] = value.startsWith('data:image') ? 'file' : 'url';
            }
          }
        }
      }
    });
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitForm.emit(this.form.getRawValue());
  }

  getErrorMessage(fieldName: string): string | null {
    const control = this.form.get(fieldName);
    if (!control || !control.errors || !control.touched) return null;

    if (control.hasError('required')) return 'Debe completar el campo.';
    if (control.hasError('minlength'))
      return 'Mínimo 3 caracteres.';
    if (control.hasError('maxlength'))
      return 'La longitud es mayor a la permitida.';
    if (control.hasError('email')) return 'Debe ingresar un correo válido.';
    if (control.hasError('min'))
      return 'El valor es menor al mínimo permitido.';
    if (control.hasError('max'))
      return 'El valor es mayor al máximo permitido.';

    return 'Campo inválido.';
  }

  handleFileChange(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.form.get(fieldName)?.setValue(file);
    this.form.get(fieldName)?.markAsDirty();

    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreviews[fieldName] = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    } else if (file) {
      this.imagePreviews[fieldName] = null;
    }
  }

  handleUppercase(fieldName: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const upper = input.value.toUpperCase();
    input.value = upper;
    this.form.get(fieldName)?.setValue(upper, { emitEvent: false });
  }

  setPickerMode(fieldName: string, mode: 'file' | 'url'): void {
    this.imagePickerMode[fieldName] = mode;
  }

  handleMacFileChange(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        this.imagePreviews[fieldName] = base64;
        this.form.get(fieldName)?.setValue(base64);
        this.form.get(fieldName)?.markAsDirty();
      };
      reader.readAsDataURL(file);
    }
  }

  handleMacUrlChange(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    const url = input.value;
    this.form.get(fieldName)?.setValue(url);
    this.imagePreviews[fieldName] = url || null;
  }
}
 
